import { useQuery } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';

import { PagedResponse, AgencyFilterDto, GenericGetFilterDto, TeamResponseDto } from '@bandido/shared/dtos';
import { QueryKeys } from '../common/types';
import { client } from '../common/services';

interface useGetAllTeamsProps extends AgencyFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetAllTeams = (params?: useGetAllTeamsProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, agencyId } = params || {};
  return useQuery({
    queryKey: [QueryKeys.getAllTeams, currentPage, pageSize, search, agencyId],
    queryFn: async (): Promise<PagedResponse<TeamResponseDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<TeamResponseDto>, any> = await client.get('team', {
          params: {
            currentPage,
            pageSize,
            search,
          },
        });

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

export const useGetTeamsByTokensAgencies = (params?: useGetAllTeamsProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback } = params || {};
  return useQuery({
    queryKey: [QueryKeys.getTeamsByTokensAgencies, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<TeamResponseDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<TeamResponseDto>, any> = await client.get(`team/agency/token`, {
          params: {
            currentPage,
            pageSize,
            search,
          },
        });

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

interface UseGetTeamById {
  id?: number;
  onErrorCallback?: (_e: AxiosError) => void;
}

interface useGetTeamsByAgencyProps extends AgencyFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
  agencyId?: string;
}

export const useGetTeamsByAgency = (params?: useGetTeamsByAgencyProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, agencyId } = params || {};
  return useQuery({
    queryKey: [QueryKeys.getAllTeams, currentPage, pageSize, search, agencyId],
    queryFn: async (): Promise<PagedResponse<TeamResponseDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<TeamResponseDto>, any> = await client.get(`team/agency/${agencyId}`, {
          params: {
            currentPage,
            pageSize,
            search,
          },
        });

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(agencyId),
  });
};

interface UseGetTeamById {
  id?: number;
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetTeamById = ({ id, onErrorCallback }: UseGetTeamById) =>
  useQuery({
    queryKey: [QueryKeys.getTeamById, id],
    queryFn: async (): Promise<TeamResponseDto | undefined> => {
      try {
        const result: AxiosResponse<TeamResponseDto, any> = await client.get(`team/${id}`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });

interface useGetTeamsByUser extends GenericGetFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
  userId?: string;
}

export const useGetTeamsByUser = (params: useGetTeamsByUser | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, userId } = params || {};
  return useQuery({
    enabled: Boolean(userId),
    queryKey: [QueryKeys.getUsersByTeam, userId, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<TeamResponseDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<TeamResponseDto>, any> = await client.get(`team/user/${userId}`, {
          params: {
            currentPage,
            pageSize,
            search,
          },
        });

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};
